/**
 * Hands a PDF blob to the browser's print dialog.
 *
 * The blob is loaded into an off-screen iframe and printed from there, so the dialog opens over the app and
 * the temporary document disappears afterwards, rather than the user being sent to another tab.
 *
 * Resolves once the dialog has been handed the document — not when printing finishes, which the browser does
 * not report. Callers use it to stop a loading indicator, not to confirm anything reached paper.
 */
export function printBlob(blob: Blob): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const iframe = createHiddenFrame();

    function discardFrame() {
      iframe.remove();
      URL.revokeObjectURL(url);
    }

    iframe.onload = () => {
      const frame = iframe.contentWindow;
      if (!frame) {
        discardFrame();
        reject(new Error("The print frame could not be opened."));
        return;
      }

      // Cleanup is best-effort: `afterprint` fires when the dialog closes, and a browser that skips it leaves
      // the frame and its object URL behind until the page is reloaded. Tearing down on a timer instead would
      // risk blanking a dialog the user still has open, which is worse than retaining a few hundred KB.
      frame.addEventListener("afterprint", discardFrame);

      try {
        frame.focus();
        frame.print();
      } catch {
        // A browser that refuses to print a PDF out of a hidden frame lands here; the caller reports it
        // rather than this helper silently opening a tab the popup blocker may swallow anyway.
        discardFrame();
        reject(new Error("The print dialog could not be opened."));
        return;
      }

      resolve();
    };

    iframe.src = url;
    document.body.appendChild(iframe);
  });
}

function createHiddenFrame(): HTMLIFrameElement {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  iframe.style.visibility = "hidden";
  return iframe;
}
